import z, {
  string,
  array,
  number,
  object,
  boolean,
  union,
  literal,
  tuple,
  discriminatedUnion,
  record,
} from "zod";

import { BreadCrumbItem } from "@/components/breadcrumbs/models";
import { LadyMinV2MainPhotoDTORaw } from "@/models/lady-dtos";

// import { CitySchemaDto } from "@/models/CitySchemaDto";
import { LadyVideoDto } from "./ladyVideo.dto";

// const PhotoDto = object({
//   mediaName: string(),
//   photoId: number(),
//   photoType: string(),
// });

export const LadyOwnPhotoRaw = LadyMinV2MainPhotoDTORaw.extend({
  type: literal("photo"),
  photoType: string().optional(),
});

export type LadyOwnPhotoRaw = z.infer<typeof LadyOwnPhotoRaw>;

export const LadyOwnPhotoSrcs = object({
  small: string(),
  medium: string(),
  large: string(),
});

type LadyOwnPhotoSrcs = z.infer<typeof LadyOwnPhotoSrcs>;

export const LadyOwnPhoto = LadyOwnPhotoRaw.extend({
  photoSrcs: LadyOwnPhotoSrcs,
});

export type LadyOwnPhoto = z.infer<typeof LadyOwnPhoto>;

export const LadyIntroductionVideo = object({
  static: string().nullish(),
  gif: string().nullish(),
  id: number().optional(),
  source: string().nullish(),
  type: literal("intro_video"),
  isFavorite: boolean().optional(),
});
export const LadyVideo = object({
  static: string().nullish(),
  gif: string().nullish(),
  id: number().optional(),
  source: string().nullish(),
  type: literal("video"),
  isFavorite: boolean().optional(),
});

export type LadyIntroductionVideo = z.infer<typeof LadyIntroductionVideo>;
export type LadyVideo = z.infer<typeof LadyVideo>;
export const LadyPrivatePhoto = LadyOwnPhoto.extend({
  PhotoType: literal("PRIVATE_PHOTO"),
});
export type LadyPrivatePhoto = z.infer<typeof LadyPrivatePhoto>;

// const PrivatePhoto = PhotoDto;

// export const LadyProfileDto = z.object({
//   age: z.number(),
//   agencyId: z.number(),
//   birthday: z.string(),
//   character: z.string(),
//   children: z.string(),
//   city: CitySchemaDto,
//   desiredDescription: z.string(),
//   drinker: z.string().optional(),
//   education: z.string().optional(),
//   englishSkill: z.string().optional(),
//   eyeColor: z.string().optional(),
//   firstName: z.string(),
//   hairColor: z.string().optional(),
//   height: z.number(),
//   hobbies: z.string(),
//   id: z.number(),
//   isGiftsAvailable: z.boolean(),
//   isOnline: z.boolean(),
//   isSmoker: z.boolean(),
//   isVideochatAvailable: z.boolean(),
//   lastName: z.string(),
//   mainPhotoID: z.number(),
//   maritalStatus: z.string().optional(),
//   mediaName: z.string(),
//   otherLanguages: z.string(),
//   ownPhotos: z.array(LadyOwnPhoto),
//   ownPrivatePhotos: z.array(PrivatePhoto),
//   privatePhotoCount: z.number(),
//   profession: z.string(),
//   publicId: z.number(),
//   registrationDate: z.string(),
//   religion: z.string().optional(),
//   status: z.string(),
//   // verticalPhotos:[{photoId: 504304, photoType: "PHOTO_LADY",…}, {photoId: 504305, photoType: "PHOTO_LADY",…},…]
//   weight: z.number(),
// });

const LadyFieldIdEnum = z.enum([
  "character",
  "interests",
  "type_of_man",
  "age",
  "religion",
  "birthday",
  "martial",
  "height",
  "children",
  "weight",
  "drinking",
  "eye",
  "smoking",
  "english",
  "occupation",
  "other_lang",
  "education",
  "hair",
]);

const LadyFieldListVariants = object({
  text: string(),
  url: string().optional(),
  inBraces: boolean().optional(),
  id: number().optional(),
});

const LadyField = object({
  header: string(),
  id: LadyFieldIdEnum,
  list: array(LadyFieldListVariants),
});

const FieldMapItemBase = object({
  header: string(),
  list: array(LadyFieldListVariants),
});

const FieldMapList = object({
  age: FieldMapItemBase,
  occupation: FieldMapItemBase,
  drinking: FieldMapItemBase,
  birthday: FieldMapItemBase,
  education: FieldMapItemBase,
  smoking: FieldMapItemBase,
  height: FieldMapItemBase,
  city: FieldMapItemBase,
  english: FieldMapItemBase,
  weight: FieldMapItemBase,
  religion: FieldMapItemBase,
  other_lang: FieldMapItemBase,
  eye: FieldMapItemBase,
  martial: FieldMapItemBase,
  hair: FieldMapItemBase,
  children: FieldMapItemBase,
});

const LadyFieldCity = object({
  header: string(),
  id: literal("city"),
  list: tuple([
    LadyFieldListVariants,
    LadyFieldListVariants.extend({ id: number() }),
  ]),
});

const BreadCrumbsRaw = object({
  text: string(),
  url: string().optional(),
});

const LadyProfileDtoFormsRaw = object({
  about: array(LadyField),
  breadCrumps: array(BreadCrumbsRaw).optional(),
  city: string(),
  description: string(),
  fields: array(discriminatedUnion("id", [LadyFieldCity, LadyField])),
  fieldsMap: FieldMapList,
  id: number(),
  name: string(),
  media: array(union([LadyOwnPhotoRaw, LadyIntroductionVideo, LadyVideo])),
  meta: record(string(), string()),
  title: string(),
  videos: array(LadyVideoDto),

  // registered uesr lady fields
  isFavorite: boolean().optional(),
  ice_breaker: boolean().optional(),
  isCamAvailable: boolean().optional(),
  isOnline: boolean().optional(),
  isGiftsAvailable: boolean().optional(),
  count_private_photo: number().optional(),
});

export const LadyProfileDtoRaw = object({
  forms: LadyProfileDtoFormsRaw,
});

export type LadyProfileDtoRaw = z.infer<typeof LadyProfileDtoRaw>;

export const LadyProfileDto = LadyProfileDtoRaw.extend({
  forms: LadyProfileDtoFormsRaw.extend({
    media: array(
      union([LadyOwnPhoto, LadyIntroductionVideo, LadyVideo, LadyPrivatePhoto]),
    ),
    breadCrumps: array(BreadCrumbItem).optional(),
  }),
  avatar: LadyOwnPhoto.optional(),
});

export type LadyProfileDto = z.infer<typeof LadyProfileDto>;
